import { RelativeTime } from './relative-time';
import { useTranslation } from 'react-i18next';

interface LicenseMetadataGridProps {
  type: string;
  createdAt: string;
  expiresAt: string | null;
  maxActivations: number;
  activationCount: number;
}

function MetaField({
  label,
  value,
  note,
  emphasis,
}: {
  label: string;
  value: React.ReactNode;
  note?: React.ReactNode;
  emphasis?: boolean;
}) {
  return (
    <div>
      <dt className="text-[13px] text-faint">{label}</dt>
      <dd
        className={`mt-1.5 text-[15px] font-medium ${
          emphasis ? 'text-status-expired' : 'text-foreground'
        }`}
      >
        {value}
      </dd>
      {note && <dd className="mt-1 text-[12px] font-medium text-status-expired">{note}</dd>}
    </div>
  );
}

export function LicenseMetadataGrid({
  type,
  createdAt,
  expiresAt,
  maxActivations,
  activationCount,
}: LicenseMetadataGridProps) {
  const { t } = useTranslation();
  const limitReached = activationCount >= maxActivations;

  return (
    <dl className="grid grid-cols-2 gap-x-8 gap-y-6 border-y py-7 sm:grid-cols-3 lg:grid-cols-5">
      <MetaField
        label={t('licenses.detail.type')}
        value={<span className="capitalize">{type}</span>}
      />
      <MetaField label={t('licenses.detail.created')} value={<RelativeTime date={createdAt} />} />
      <MetaField label={t('licenses.detail.expires')} value={<RelativeTime date={expiresAt} />} />
      <MetaField
        label={t('licenses.detail.maxActivations')}
        value={<span className="font-mono text-[14px] tabular-nums">{maxActivations}</span>}
      />
      <MetaField
        label={t('licenses.detail.activationsUsed')}
        emphasis={limitReached}
        value={
          <span
            className={`font-mono text-[14px] tabular-nums ${limitReached ? 'font-semibold' : ''}`}
            data-testid="activations-used"
          >
            {activationCount}
          </span>
        }
        note={limitReached ? t('licenses.detail.limitReached') : undefined}
      />
    </dl>
  );
}
